import { GeneratedAttachmentResponse } from "@/Common/Api/Attachments/Dto/Responses/GeneratedAttachmentResponse";
import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ImportedPatientMappingResponse } from "@/Common/Api/ExternalCommunications/Dto/ImportedPatientMappingResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PatientImportComponent } from "@/Common/Components/Import/PatientImportComponent";
import { ExternalCommunicationPatientImportHandler } from "@/Common/Components/Import/PatientImportHandler";
import { IImportablePatient } from "@/Common/Models/Patient/IPatient";
import { IJPatientCreationModel, IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DocumentImportPopupComponent } from "@/Communications/Components/ExternalCommunications/Import/DocumentImportPopupComponent";
import { DocumentImportInfo } from "@/Communications/Models/DocumentImportInfo";

interface IImportWizardProps {
    componentId: string;
    patient: IImportablePatient;
    generatedAttachment?: GeneratedAttachmentResponse;
    documentDate?: Date;
    documentSource?: string;
    responsibleProviderContactFacetIds?: string[];

    onClose: () => void;
    onImportSuccessful?: () => void;
    onErrorOkClick?: () => void;
}

class ImportWizardState {
    public importedPatients: ImportedPatientMappingResponse[];
    public showPatientImportComponent: boolean;
    public showDocumentImportComponent: boolean;

    public patientContactFacetIdToLink: string;
    public patientCreationModel: IPatientCreationModel;

    public searchText?: string;
    public includeDeactivatedPatients?: boolean;
}

class ImportWizardComponent extends BaseReactComponent<IImportWizardProps, ImportWizardState> {
    constructor(props: IImportWizardProps) {
        super(props);

        const initialState = new ImportWizardState();
        this.goToImportPatientStep(initialState);
        this.state = initialState;
    }

    public componentDidMount(): void {
        this.loadImportedPatients(this.props.patient.ClientTag);
    }

    public render(): JSX.Element {
        if (!this.state.importedPatients)
            return null;

        return (
            <div id={this.props.componentId}>
                {this.state.showPatientImportComponent &&
                    this.renderPatientImportComponent()
                }
                {this.state.showDocumentImportComponent &&
                    this.renderDocumentImportComponent()
                }
            </div>
        );
    }

    private renderPatientImportComponent(): JSX.Element {
        if (!this.hasAttachment || !DocumentImportPopupComponent.hasImportDocumentPermission)
            return this.renderStandAlonePatientImport();

        return this.renderPatientImportWithDocumentImport();
    }

    private renderStandAlonePatientImport(): JSX.Element {
        return (
            <PatientImportComponent
                componentId="PatientImportComponent"
                handler={new ExternalCommunicationPatientImportHandler(this.state.importedPatients)}
                patient={this.props.patient}
                linkLabel="LinkLabel"
                createLabel="CreateLabel"
                enablePatientCreation={cacheStore.ClinicSettings.EnablePatientEdit}
                onClose={() => this.onClose()}
                onErrorOkClick={() => this.onErrorOkClick()}
                onLink={existingPatientContactFacetId => this.handleStandAlonePatientImportOnLink(existingPatientContactFacetId)}
                onCreate={patientCreationModel => this.handleStandAlonePatientImportOnCreate(patientCreationModel)}
                duplicateCheckOnLinkEnabled
            />
        );
    }

    private renderPatientImportWithDocumentImport(): JSX.Element {
        return (
            <PatientImportComponent
                componentId="PatientImportComponent"
                handler={new ExternalCommunicationPatientImportHandler(this.state.importedPatients)}
                patient={this.props.patient}
                searchText={this.state.searchText}
                patientCreationModel={this.state.patientCreationModel}
                includeDeactivatedPatients={this.state.includeDeactivatedPatients}
                linkLabel="ContinueLabel"
                createLabel="ContinueLabel"
                width={this.popupWidth}
                enablePatientCreation={cacheStore.ClinicSettings.EnablePatientEdit}
                onClose={() => this.onClose()}
                onErrorOkClick={() => this.onErrorOkClick()}
                onLink={(existingPatientContactFacetId, searchText, includeDeactivatedPatient) => this.handlePatientImportWithDocumentOnLink(
                    existingPatientContactFacetId,
                    searchText,
                    includeDeactivatedPatient
                )}
                onCreate={patientCreationModel => this.handlePatientImportWithDocumentOnCreate(patientCreationModel)}
                duplicateCheckOnLinkEnabled
            />
        );
    }

    private renderDocumentImportComponent(): JSX.Element {
        return (
            <DocumentImportPopupComponent
                generatedAttachment={this.props.generatedAttachment}
                siteId={cacheStore.GetCurrentEmployee().ActiveSiteId}
                responsibleClinicianContactFacetIds={this.props.responsibleProviderContactFacetIds}
                documentDate={this.props.documentDate}
                source={this.props.documentSource}

                hideHeaderCloseOption={true}
                cancelLabel="BackLabel"
                width={this.popupWidth}
                onClose={() => this.mutateState(s => {
                    s.showPatientImportComponent = true;
                    s.showDocumentImportComponent = false;
                })}
                onImport={document => this.handleOnImportDocument(document)}
                onImportPlusApprove={document => this.handleOnImportDocument(document, true)}
            />
        );
    }

    private get hasAttachment(): boolean {
        return !!this.props.generatedAttachment;
    }

    private get popupWidth(): number {
        return 960;
    }

    private handleStandAlonePatientImportOnLink(existingPatientContactFacetId: string): void {
        this.handleImportPatient(null, existingPatientContactFacetId);
    }

    private handleStandAlonePatientImportOnCreate(patientCreationModel: IPatientCreationModel): void {
        this.handleImportPatient(patientCreationModel, null);
    }

    private handlePatientImportWithDocumentOnLink(existingPatientContactFacetId: string, searchText: string, includeDeactivatedPatients: boolean): void {
        this.mutateState(s => {
            s.patientContactFacetIdToLink = existingPatientContactFacetId;
            s.searchText = searchText;
            s.includeDeactivatedPatients = includeDeactivatedPatients;
            s.patientCreationModel = null;

            this.goToImportDocumentStep(s);
        });
    }

    private handlePatientImportWithDocumentOnCreate(patientCreationModel: IPatientCreationModel): void {
        this.mutateState(s => {
            s.patientCreationModel = patientCreationModel;
            s.searchText = null;
            s.includeDeactivatedPatients = null;
            s.patientContactFacetIdToLink = null;

            this.goToImportDocumentStep(s);
        });
    }

    private async handleImportPatient(patient: IPatientCreationModel, internalPatientContactFacetId: string): Promise<void> {
        try {
            await this.importPatient(patient, internalPatientContactFacetId);
            this.onSuccess();
        }
        catch (error) {
            this.handleError(error);
        }
    }

    private async handleOnImportDocument(documentInfo: DocumentImportInfo, shouldApproveDocument: boolean = false): Promise<void> {
        try {
            documentInfo.patientContactId = await this.importPatient(this.state.patientCreationModel, this.state.patientContactFacetIdToLink);
            await this.importDocument(documentInfo, shouldApproveDocument);
            this.onSuccess();
        }
        catch (error) {
            this.handleError(error);
        }
    }

    private importPatient(patient: IPatientCreationModel, internalPatientContactFacetId: string): Promise<string> {
        let createPatientRequest = null;
        if (patient) {
            createPatientRequest = {
                SelectedPrefixLookupId: patient.get("Prefix"),
                FirstName: patient.get("FirstName"),
                PreferredName: patient.get("PreferredName"),
                LastName: patient.get("LastName"),
                GenderLookupId: patient.get("GenderLookupId"),
                DateOfBirth: patient.get("DOB"),
                CivicAddress: patient.get("CivicAddressEdit"),
                City: patient.get("CityEdit"),
                PostalCode: patient.get("PostalCodeEdit"),
                NAM: patient.get("NAMEdit"),
                NAMExpiry: patient.get("NAMExpiryEdit"),
                CountryLookupId: patient.get("SelectedCountryLookupId"),
                RegionLookupId: patient.get("SelectedRegionLookupId"),
                Phone1Number: patient.get("Phone1Edit"),
                Phone1ContactTypeLookupId: patient.get("Phone1ContactTypeLookupId"),
                Phone1TypeLookupId: patient.get("Phone1TypeLookupId"),
                Phone1Ext: patient.get("Phone1Extension"),
                Phone1NotificationsEnabled: patient.get("Phone1NotificationsEnabled"),
                Phone2Number: patient.get("Phone2Edit"),
                Phone2ContactTypeLookupId: patient.get("Phone2ContactTypeLookupId"),
                Phone2TypeLookupId: patient.get("Phone2TypeLookupId"),
                Phone2Ext: patient.get("Phone2Extension"),
                Phone2NotificationsEnabled: patient.get("Phone2NotificationsEnabled"),
                EmailAddress: patient.get("EmailEdit"),
                EmailTypeByLookup: patient.get("EmailTypeByLookup"),
                IsOkToContact: patient.get("IsOkToContact"),
                EmailNotificationsEnabled: patient.get("EmailNotificationsEnabled"),
                PreferredSpokenLanguageLookupId: patient.get("PreferredSpokenLanguageLookupId"),
                MotherLastName: patient.get("motherLastName"),
                MotherFirstName: patient.get("motherFirstName"),
                HealthCardRegionLookupId: IJPatientCreationModel.getHealthCardRegionLookupId(patient)
            };
        }

        const requestData = {
            CreatePatientRequest: createPatientRequest,
            InternalPatientId: internalPatientContactFacetId,
            ExternalPatientId: this.props.patient.PatientId,
            ExternalPatientClientTag: this.props.patient.ClientTag
        };

        return new Promise((resolve, reject) => {
            Client.Postv2(
                ServerOperations.Server.Myle_ExternalCommunication_ImportPatientProfile,
                requestData,
                response => resolve(response.PatientContactId),
                errorMsg => reject(errorMsg)
            );
        });
    }

    private importDocument(documentImportInfo: DocumentImportInfo, shouldApproveDocument: boolean): Promise<void> {
        let requestData = {
            ResponsibleProviderContactIds: documentImportInfo.responsibleClinicianContactIds,
            Source: documentImportInfo.source,
            Description: documentImportInfo.description,
            Note: documentImportInfo.note,
            DocumentDate: documentImportInfo.documentDate,
            IsUrgent: false,
            ClassLookupId: documentImportInfo.classLookupId,
            SubclassLookupId: documentImportInfo.subClassLookupId,
            PatientContactId: documentImportInfo.patientContactId,
            SiteId: documentImportInfo.siteId,
            GeneratedAttachment: documentImportInfo.generatedAttachment,
            ShouldApproveDocument: shouldApproveDocument
        };

        return new Promise((resolve, reject) => {
            Client.Postv2(
                ServerOperations.Server.Myle_ExternalCommunication_ImportDocument,
                requestData,
                () => resolve(),
                errorMsg => reject(errorMsg)
            );
        });
    }

    private loadImportedPatients(clientTag: string): void {
        if (this.state.importedPatients || !clientTag)
            return;

        ExternalCommunicationPatientImportHandler
            .loadImportedPatientMapping(clientTag)
            .then(importedPatients => this.mutateState(s => s.importedPatients = importedPatients))
            .catch(error => this.handleError(error));
    }

    private handleError(errors: ErrorMessage[]): void {
        if (isArrayNullOrUndefinedOrEmpty(errors))
            return;

        sessionStore.showPopup({
            content: errors,
            okAction: () => this.onErrorOkClick()
        });

        this.onClose();
    }

    private goToImportDocumentStep(state: ImportWizardState): void {
        state.showPatientImportComponent = false;
        state.showDocumentImportComponent = true;
    }

    private goToImportPatientStep(state: ImportWizardState): void {
        state.showPatientImportComponent = true;
        state.showDocumentImportComponent = false;
    }

    private onClose(): void {
        this.props.onClose && this.props.onClose();
    }

    private onSuccess(): void {
        this.props.onImportSuccessful && this.props.onImportSuccessful();
    }

    private onErrorOkClick(): void {
        this.props.onErrorOkClick ? this.props.onErrorOkClick() : this.props.onClose();
    }
}

export { ImportWizardComponent };
